/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { AddToCartBehavior, CartIconComponent } from '@msdyn365-commerce/components';
import { getUrlSync, IDictionary, IImageSettings, msdyn365Commerce, TelemetryEvent } from '@msdyn365-commerce/core';
import {
    CartLine,
    ChannelDeliveryOptionConfiguration,
    CartLineValidationResultsByLineId,
    OrgUnitLocation,
    LineValidationResult,
    AsyncResult
} from '@msdyn365-commerce/retail-proxy';
import { validateCartAsync } from '@msdyn365-commerce-modules/cart-utilities';
import { ArrayExtensions, ObjectExtensions, validateCartLinesInventory } from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    INodeProps,
    ITelemetryContent,
    TelemetryConstant,
    UncontrolledTooltip,
    updateMaxQuantityForCartLineItem,
    urlCheck
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Image } from '@msdyn365-commerce/core';

import { IExtCartIconData } from './ext-cart-icon.data';
import { cartLinesSortOrder, IExtCartIconProps } from './ext-cart-icon.props.autogenerated';
import { FlyoutCartLineItems, IFlyoutCartLineItemViewProps } from './components/flyout-cart-line-items';
import { cartLineQuantityUpdatedAsync } from '@msdyn365-commerce-modules/cart-utilities';
import { getMiniCartData, IMiniCartData } from './utils/get-products-in-mini-cart';
import ModalPanel, { Position } from '../../shared/components/modal-panel/modal-panel.component';
import { ICartActionResult } from '@msdyn365-commerce/global-state';
import CartChangePopup from './components/cart-change-popup';
import { useCartChangeDetection } from './hooks/use-cart-change-detection';

export interface IMiniCartState {
    miniCartData: IMiniCartData;
    orgUnitLocations?: OrgUnitLocation[];
    isQuantityLimitsFeatureEnabled: boolean;
    isCartValid: boolean;
    cartlinesErrors: CartLineValidationResultsByLineId;
    freeDeliveryReached: boolean;
    isPopupOpen: boolean;
}

export interface ICartIconViewProps extends IExtCartIconProps<IExtCartIconData> {
    className: string;
    cartIcon: React.ReactNode;
    FlyoutContainer?: INodeProps;
    CartlinesWrapper: INodeProps;
    miniCartWrapper: INodeProps;
    miniCartItemWrapper: INodeProps;
    cartlines?: IFlyoutCartLineItemViewProps[];
    flyoutTitle: React.ReactNode;
    totalPrice: React.ReactNode;
    checkoutAsGuestButton?: React.ReactNode;
    checkoutAsSignInUserButton?: React.ReactNode;
    goToCartButton?: React.ReactNode;
    waitingComponent?: React.ReactNode;
    isCartLoading: boolean;
    checkoutBlockedDueToUnavailableFunds?: React.ReactNode;
    freeDeliveryCountdown: React.ReactNode;
    subTotalMessage: React.ReactNode;
    popupContent?: React.ReactNode;
    paymentOptionsImgs?: React.ReactNode;
    areCartLinesInitialized: boolean;

    removeItemClickHandler(cartlineToRemove: CartLine): void;
}

/**
 *
 * Cart component.
 * @extends {React.PureComponent<ICartProps<ICartData>>}
 */
@observer
class CartIcon extends React.Component<ICartIconViewProps, IMiniCartState> {
    private readonly telemetryContent: ITelemetryContent;

    private readonly checkoutBySignInAttributes: IDictionary<string> | undefined;

    private readonly checkoutByGuestAttributes: IDictionary<string> | undefined;

    private readonly viewShoppingBagAttributes: IDictionary<string> | undefined;

    private retailMultiplePickUpOptionEnabled?: boolean = false;

    private channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    private lastValidatedCartVersion: number | undefined;

    constructor(props: ICartIconViewProps) {
        super(props);
        this.state = {
            miniCartData: {
                isInitialized: false
            },
            isCartValid: true,
            isQuantityLimitsFeatureEnabled: false,
            cartlinesErrors: {},
            freeDeliveryReached: false,
            isPopupOpen: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const payload = getPayloadObject(TelemetryEvent.CheckOut, this.telemetryContent, TelemetryConstant.Checkout);
        this.checkoutBySignInAttributes = getTelemetryAttributes(this.telemetryContent, payload);

        payload.contentAction.etext = TelemetryConstant.GuestCheckout;
        this.checkoutByGuestAttributes = getTelemetryAttributes(this.telemetryContent, payload);

        payload.contentAction.etext = TelemetryConstant.ViewShoppingBag;
        this.viewShoppingBagAttributes = getTelemetryAttributes(this.telemetryContent, payload);
    }

    public async componentDidMount(): Promise<void> {
        const context = this.props.context.actionContext;

        reaction(
            () => this.props.data.cart.result?.cart.CartLines,
            async () => {
                this.setState({
                    miniCartData: await getMiniCartData(context, this.props.data.cart.result!)
                });
            }
        );
        await this._setQuantityLimitsFeatureFlag();

        this.setState({
            orgUnitLocations: this.props.data.orgUnitLocations?.result
        });

        this._validateCart();
    }

    public componentDidUpdate(prevProps: ICartIconViewProps): void {
        this._validateCart();
    }

    public shouldComponentUpdate(nextProps: ICartIconViewProps, nextState: IMiniCartState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const {
            id,
            typeName,
            config: {
                enableHoverCart,
                isAnonymousCheckout,
                showCheckoutButton,
                showCartButton,
                showSubtotalMessage,
                showFreeDeliveryText,
                paymentImages,
                showPaymentImages
            },
            context: {
                request: {
                    user: { isAuthenticated, signInUrl }
                }
            },
            context,
            resources
        } = this.props;

        const cart = (this.props.data.cart && this.props.data.cart.result) || undefined;
        const guestCheckoutUrl = getUrlSync('checkout', this.props.context.actionContext) || '';
        const cartUrl = getUrlSync('cart', this.props.context.actionContext) || '';
        const checkoutURL = this._getCheckoutURL(isAuthenticated, guestCheckoutUrl, signInUrl || '');
        const isCartEmpty = cart && cart.cart.CartLines && cart.cart.CartLines.length === 0;
        const shouldShowMiniCart =
            cart?.isProductAddedToCart && this.props.context.app.config.addToCartBehavior === AddToCartBehavior.showMiniCart;
        const hasInvoiceLine = cart?.hasInvoiceLine;
        const emailDeliveryModeCode = this.props.context.request.channel?.EmailDeliveryModeCode;
        const cartLines = this._getCartLines();
        const validInventoryAcrossCartLines = validateCartLinesInventory(
            cart?.cart.CartLines ?? [],
            this.state.miniCartData.productAvailabilites ?? [],
            this.props.context.actionContext,
            this.state.miniCartData.products ?? [],
            emailDeliveryModeCode
        );
        const hasError =
            !validInventoryAcrossCartLines?.isValid ||
            (!validInventoryAcrossCartLines && !this.state.isCartValid) ||
            (cartLines ? cartLines.some(cartline => cartline.hasError) : false);
        const includeFlyout = this.props.data.cart && this.props.data.cart.status === 'SUCCESS' && enableHoverCart;
        const totalItemsInCart = cart?.totalItemsInCart ? cart.totalItemsInCart : 0;
        const goToCartButtonText = resources.goToCartButtonTitle.replace('{count}', totalItemsInCart.toString());
        const flyoutTitle = resources.flyoutTitle;
        const flyoutTooltip = {
            tag: UncontrolledTooltip,
            shouldShowMiniCart,
            placement: 'bottom-end',
            hideArrow: true,
            className: 'ms-cart-icon__flyout-container',
            trigger: 'hover',
            displayMode: 'FLYOUT',
            target: id,
            shouldShowCloseButton: true,
            cartPageUrl: cartUrl,
            hideAriaHidden: true
        };

        // If there are invoice lines, don't update mini cart price
        const price = (cart && !cart.hasInvoiceLine && cart.cart.TotalAmount) || undefined;
        const priceString = price
            ? resources.totalPriceFormatString.replace('{price}', context.cultureFormatter.formatCurrency(price))
            : '';
        const cartTotalAmount = (cart && cart.cart.TotalAmount) || 0;

        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        this.channelDeliveryOptionConfig = this.props.data.channelDeliveryOptionConfig?.result;
        this.retailMultiplePickUpOptionEnabled = this.props.data.featureState?.result?.find(
            feature => feature.Name === multiplePickupStoreSwitchName
        )?.IsEnabled;

        const viewProps = {
            ...this.props,
            cartIcon: (
                <CartIconComponent
                    cartLabel={resources.cartLabel}
                    cartQtyLabel={resources.cartQtyLabel}
                    context={context}
                    id={id}
                    typeName={typeName}
                    data={{ cart }}
                    telemetryContent={this.telemetryContent}
                />
            ),
            areCartLinesInitialized: this.state.miniCartData.isInitialized,
            cartlines: cartLines,
            FlyoutContainer: includeFlyout ? flyoutTooltip : undefined,
            CartlinesWrapper: {
                className: 'ms-cart-icon__cart-lines'
            },
            miniCartWrapper: {
                className: classnames('ms-cart-icon', { 'ms-cart-icon--has-items': totalItemsInCart > 0 }),
                id: this.props.id
            },
            miniCartItemWrapper: {
                className: classnames('msc-cart-lines-item')
            },
            // If there are invoice lines, don't update mini cart checkout button
            // Checkout button will display by default to maintain backward compatibility.  When Anonymous user checkout is enabled &&
            // user is authenticated then we will show the checkout button otherwise we will show the guest checkout button.
            //BS CUSTOM
            freeDeliveryCountdown: showFreeDeliveryText ? (
                <div>
                    <div className='ms-cart-icon__free-delivery-countdown'>{this._getFreeDeliveryCount(cartTotalAmount)}</div>
                    <progress
                        className='ms-cart-icon__freeDeliveryProgress'
                        value={cartTotalAmount}
                        max={isAuthenticated ? this.props.config.freeDeliveryAmountMember : this.props.config.freeDeliveryAmountGuest}
                    ></progress>
                </div>
            ) : null,
            //NOTE: end of custom BS CUSTOM
            checkoutAsSignInUserButton:
                !isCartEmpty && !hasInvoiceLine && showCheckoutButton && (!isAnonymousCheckout || isAuthenticated) ? (
                    <Button
                        disabled={hasError}
                        className='ms-cart-icon__btn-checkout'
                        href={checkoutURL}
                        title={this.props.config.checkoutButtonTitle}
                        {...this.checkoutBySignInAttributes}
                    >
                        {this.props.config.checkoutButtonTitle ?? 'checkout'}
                    </Button>
                ) : null,
            checkoutAsGuestButton:
                isAnonymousCheckout && !isCartEmpty && showCheckoutButton && !isAuthenticated ? (
                    <Button
                        disabled={hasError}
                        className='ms-cart-icon__btn-guestcheckout'
                        href={guestCheckoutUrl}
                        title={this.props.config.checkoutButtonTitle}
                        {...this.checkoutByGuestAttributes}
                    >
                        {this.props.config.checkoutButtonTitle ?? 'checkout'}
                    </Button>
                ) : null,
            goToCartButton: showCartButton && (
                <Button
                    className='ms-cart-icon__btn-gotocart'
                    href={cartUrl}
                    title={goToCartButtonText}
                    {...this.viewShoppingBagAttributes}
                >
                    {goToCartButtonText}
                </Button>
            ),
            checkoutBlockedDueToUnavailableFunds: null,
            flyoutTitle: <span className='ms-cart-icon__flyout-title'>{flyoutTitle}</span>,
            paymentOptionsImgs: showPaymentImages && paymentImages && (
                <div className='ms-cart-icon__payment-images'>
                    {paymentImages?.map((img, index) => {
                        return (
                            <Image
                                src={img.src}
                                alt={img.title}
                                title={img.title}
                                key={`${img.title}_${index}`}
                                imageSettings={this._getPaymentImageSettings(img.imageSettings)}
                                gridSettings={context.request.gridSettings!}
                            />
                        );
                    })}
                </div>
            ),
            totalPrice: <div className='ms-cart-icon__subtotal'>{priceString}</div>,
            popupContent: this._getPopupContent(),
            subTotalMessage: showSubtotalMessage ? <p className='ms-cart-icon__subtotal-message'>{this._setSubtotalMessage()}</p> : null
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }
    //BS CUSTOM
    private _getFreeDeliveryCount(cartTotalAmount: number): string {
        const {
            config: { freeDeliveryAmountGuest, freeDeliveryAmountMember, freeDeliveryReachedText, untilFreeDeliveryText },
            context: {
                request: {
                    user: { isAuthenticated }
                }
            }
        } = this.props;

        let amountTillFreeDel = 0;
        const freelDeliveryAmount = isAuthenticated ? freeDeliveryAmountMember : freeDeliveryAmountGuest;
        let finalString = '';

        if (freelDeliveryAmount !== undefined && untilFreeDeliveryText && cartTotalAmount !== undefined) {
            if (cartTotalAmount < freelDeliveryAmount) {
                amountTillFreeDel = parseFloat(freelDeliveryAmount.toFixed(2)) - parseFloat(cartTotalAmount.toFixed(2));
                finalString = untilFreeDeliveryText?.replace(
                    '{amount}',
                    this.props.context.cultureFormatter.formatCurrency(amountTillFreeDel)
                );

                if (this.state.freeDeliveryReached !== false) {
                    this.setState({ freeDeliveryReached: false });
                }
            } else {
                finalString = freeDeliveryReachedText ?? '';
                if (this.state.freeDeliveryReached !== true) {
                    this.setState({ freeDeliveryReached: true });
                }
            }
        }

        return finalString;
    }

    private _setSubtotalMessage(): JSX.Element[] | JSX.Element {
        let text = '';
        let res = <></>;
        if (this.state.freeDeliveryReached) {
            text = this.props.config.subtotalMessageOnFreeShipping ?? '';
        } else {
            text = this.props.config.subtotalMessageUntilFreeShipping ?? '';
        }

        if (text.includes('|')) {
            res = (
                <>
                    {text.split('|').map((part, index) => (
                        <span key={index}>{part}</span>
                    ))}
                </>
            );
        } else {
            res = <span>{text}</span>;
        }
        return res;
    }

    //BS CUSTOM
    private _getPopupContent = (): JSX.Element => {
        const popupContentSlot = this.props.slots.popupContent;
        if (popupContentSlot) {
            const toggleModal = () => {
                this.setState({ isPopupOpen: !this.state.isPopupOpen });
            };

            return (
                <>
                    <button className='popup-button' onClick={toggleModal}>
                        {this.props.config.popupContentText?.includes('|')
                            ? this.props.config.popupContentText.split('|').map((part, index) => (
                                  <span key={index}>
                                      {part}
                                      {index === 0 && <br />}
                                  </span>
                              ))
                            : this.props.config.popupContentText}
                    </button>
                    <ModalPanel position={Position.RIGHT} isOpen={this.state.isPopupOpen} toggle={toggleModal}>
                        <button className='close-button' onMouseDown={toggleModal}></button>
                        {popupContentSlot}
                    </ModalPanel>
                </>
            );
        }
        return <></>;
    };

    private _validateCart(): Promise<void> {
        const {
            data: { cart }
        } = this.props;
        const cartVersion = cart.result?.cart?.Version ?? undefined;
        if (this.lastValidatedCartVersion === cartVersion) {
            return AsyncResult.resolve();
        }

        this.lastValidatedCartVersion = cartVersion; // This field is populated to block concurrent requests

        if (this.state.isQuantityLimitsFeatureEnabled) {
            validateCartAsync(cart, this.props.telemetry, this.props.context)
                .then((result: CartLineValidationResultsByLineId | undefined) => {
                    this.setState({
                        cartlinesErrors: result ?? {},
                        isCartValid: !ArrayExtensions.hasElements(result?.ValidationResultsPairs)
                    });
                })
                .catch(() => {
                    this.setState({ isCartValid: false });
                });
        } else {
            this.setState({ isCartValid: true });
        }

        return AsyncResult.resolve();
    }

    private _getCheckoutURL(isAuthenticated: boolean, guestCheckoutUrl: string, signInUrl: string): string {
        if (isAuthenticated) {
            return guestCheckoutUrl;
        }

        const isAbsoluteUrl = urlCheck(guestCheckoutUrl);
        let returnURL = guestCheckoutUrl;
        if (msdyn365Commerce.isBrowser && !isAbsoluteUrl) {
            returnURL = `${window.location.origin}${guestCheckoutUrl}`;
        }

        return `${signInUrl}${!signInUrl.includes('?') ? '?' : '&'}ru=${returnURL}`;
    }

    /**
     * Sort cartline items.
     * @param cartlines - Cartlines to be sorted.
     * @param sortOrder - Ascending or descending.
     * @returns - Sorted cartlines.
     */
    private readonly getSortedCartLines = (cartlines: CartLine[], sortOrder?: cartLinesSortOrder): CartLine[] => {
        const lines: CartLine[] = [...cartlines];
        const zero: number = 0;

        if (sortOrder === cartLinesSortOrder.descending) {
            return lines.sort((item1, item2) => (item2.LineNumber ?? zero) - (item1.LineNumber ?? zero));
        }
        return lines.sort((item1, item2) => (item1.LineNumber ?? zero) - (item2.LineNumber ?? zero));
    };

    private _getCartLines(): IFlyoutCartLineItemViewProps[] | null {
        const {
            context: {
                request: { channel: { PickupDeliveryModeCode } = { PickupDeliveryModeCode: undefined } }
            },
            config,
            telemetry,
            resources
        } = this.props;

        const imageSettings = this._getImageSettings(config.imageSettings);
        const cart = this.props.data.cart && this.props.data.cart.result;
        const cartlinesErrors: CartLineValidationResultsByLineId = this.state.cartlinesErrors;

        // Cart-icon could be placed in each page
        // so we check cart content to reduce number of RS calls
        return this.state.miniCartData.products && cart && cart.cart.CartLines && cart.cart.CartLines.length > 0
            ? FlyoutCartLineItems({
                  cartlines: this.getSortedCartLines(cart.cart.CartLines, config.cartLinesSortOrder),
                  products: this.state.miniCartData.products,
                  productAvailabilites: this.state.miniCartData.productAvailabilites,
                  productDeliveryOptions: this.state.miniCartData.deliveryOptions,
                  imageSettings,
                  outOfStockThreshold: this.props.context.app.config.outOfStockThreshold || 0,
                  isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                  maxCartlineQuantity: updateMaxQuantityForCartLineItem(this.props.context.app.config.maxQuantityForCartLineItem),
                  gridSettings: this.props.context.request.gridSettings!,
                  context: this.props.context,
                  typeName: this.props.typeName,
                  id: this.props.id,
                  resources: {
                      sizeString: resources.productDimensionTypeSizeFormat,
                      colorString: resources.productDimensionTypeColorFormat,
                      configString: resources.productDimensionTypeConfiguration,
                      styleString: resources.productDimensionTypeStyle,
                      quantityDisplayString: resources.quantityDisplayText,
                      salesAgreementPricePrompt: resources.salesAgreementPricePrompt,
                      inputQuantityAriaLabel: '', // Not used
                      discountStringText: '', // Not used
                      originalPriceText: '',
                      currentPriceText: '',
                      shippingChargesText: '',
                      shippingText: resources.shippingText,
                      saleMessage: resources.saleMessage,
                      decrementButtonAriaLabel: '',
                      incrementButtonAriaLabel: ''
                  },
                  telemetry,
                  removeButtonText: resources.removeCartButtonText,
                  outOfStockText: '', // Not used
                  outOfRangeOneText: '', // Not used
                  outOfRangeFormatText: '', // Not used
                  cartState: cart,
                  orgUnitLocations: this.state.orgUnitLocations,
                  updateCartLinesQuantitySuccessHandler: this.updateCartlineQuantity,
                  removeItemClickHandler: this._removeItemFromCartHandler,
                  pickupDeliveryModeCode: PickupDeliveryModeCode,
                  telemetryContent: this.telemetryContent,
                  channelDeliveryOptionConfig: this.retailMultiplePickUpOptionEnabled ? this.channelDeliveryOptionConfig : undefined,
                  isQuantityLimitsFeatureEnabled: this.state.isQuantityLimitsFeatureEnabled,
                  catalogs: this.props.data.catalogs?.result,
                  cartlinesErrors: cartlinesErrors
              })
            : null;
    }

    private readonly _getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return (
            imageSettings || {
                viewports: {
                    xs: { q: 'w=80&h=94&m=6', w: 0, h: 0 },
                    sm: { q: 'w=148&h=174&m=6', w: 0, h: 0 },
                    lg: { q: 'w=148&h=174&m=6', w: 0, h: 0 }
                },
                lazyload: true
            }
        );
    };

    //BS CUSTOM
    private readonly _getPaymentImageSettings = (paymentImageSettings: IImageSettings | undefined): IImageSettings => {
        return (
            //"xs": 767, "sm": 991, "md": 1199, "lg": 1599, "xl": 1600
            paymentImageSettings || {
                viewports: {
                    xs: { q: 'w=auto&h=40&m=6', w: 0, h: 0 },
                    lg: { q: 'w=auto&h=40&m=6', w: 0, h: 0 }
                },
                lazyload: true,
                mode: 6
            }
        );
    };

    private readonly _removeItemFromCartHandler = (cartlineToRemove: CartLine) => {
        if (this.props.data.cart.result) {
            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };
            this.props.data.cart.result.removeCartLines(input).catch(error => {
                this.props.telemetry.warning(error);
                this.props.telemetry.debug('Unable to Remove Cart Line');
            });
        }
    };

    /**
     * Updates the cart line quantity.
     * @param cartlineToUpdate - The cart line.
     * @param quantity - The quantity.
     * @param lineIndex - The line index.
     * @returns True if the cart line was updated, false otherwise.
     */
    private readonly updateCartlineQuantity = (cartlineToUpdate: CartLine, quantity: number, lineIndex?: number): boolean => {
        cartLineQuantityUpdatedAsync(
            this.state.isQuantityLimitsFeatureEnabled,
            this.props.data.cart.result,
            this.props.telemetry,
            this.state.miniCartData.products,
            cartlineToUpdate,
            quantity
        )
            .then((cartActionResult: ICartActionResult | undefined): boolean => {
                if (cartActionResult?.status === 'SUCCESS') {
                    this._validateCart().catch(error => {
                        this.props.telemetry.warning((error as Error).message);
                        this.props.telemetry.debug('Unable to validate Cart');
                    });

                    return true;
                }

                if (!ObjectExtensions.isNullOrUndefined(cartActionResult)) {
                    // Show update line error message.
                    const lineUpdateFailure: LineValidationResult = {
                        LineId: ObjectExtensions.isNullOrUndefined(cartlineToUpdate.LineId) ? '0' : cartlineToUpdate.LineId,
                        ValidationFailures: [
                            {
                                ErrorContext: ArrayExtensions.hasElements(cartActionResult.validationResults)
                                    ? cartActionResult.validationResults[0].ErrorContext
                                    : '',
                                LocalizedMessage: cartActionResult.errorDetails?.LocalizedMessage
                            }
                        ]
                    };

                    this.setState({
                        cartlinesErrors: { ValidationResultsPairs: [lineUpdateFailure] }
                    });
                }

                return false;
            })
            .catch(() => {
                return false;
            });

        return false;
    };

    private async _setQuantityLimitsFeatureFlag(): Promise<void> {
        const featureStatuses = await this.props.data.featureState;

        const isChannelQuantityLimitsFeatureEnabledInHq =
            featureStatuses?.find(
                featureState => featureState.Name === 'Dynamics.AX.Application.EcommerceQuantityLimitConfigurationsFeature'
            )?.IsEnabled || false;
        if (isChannelQuantityLimitsFeatureEnabledInHq) {
            this.setState({ isQuantityLimitsFeatureEnabled: true });
            return;
        }

        const useSiteBuilderSettings = featureStatuses?.find(
            featureState => featureState.Name === 'RevertToSiteBuilderOrderQuantityLimitsSettings'
        )?.IsEnabled;

        if (useSiteBuilderSettings) {
            const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
            if (!defaultOrderQuantityLimitsFeatureConfig || defaultOrderQuantityLimitsFeatureConfig === 'none') {
                this.setState({ isQuantityLimitsFeatureEnabled: false });
                return;
            }

            try {
                const featureStatuses = await this.props.data.featureState;
                const isQuantityLimitsFeatureEnabledInHq =
                    featureStatuses?.find(
                        featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
                    )?.IsEnabled || false;
                if (!isQuantityLimitsFeatureEnabledInHq) {
                    this.setState({ isQuantityLimitsFeatureEnabled: false });
                    return;
                }
            } catch (error) {
                if (typeof error === 'string') {
                    this.props.telemetry.warning(error);
                }
                if (error instanceof Error) {
                    this.props.telemetry.warning(error.message);
                }
                this.props.telemetry.debug('Unable to get feature states');
                this.setState({ isQuantityLimitsFeatureEnabled: false });
                return;
            }
            if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
                this.setState({ isQuantityLimitsFeatureEnabled: true });
            } else {
                this.props.data.customerInformation
                    .then(customerInfo => {
                        const result =
                            !!customerInfo &&
                            ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                                (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b));
                        this.setState({ isQuantityLimitsFeatureEnabled: result });
                    })
                    .catch(error => {
                        this.props.telemetry.warning(error);
                        this.props.telemetry.debug('Unable to get customer info');
                    });
            }
        } else {
            const cartConfiguration = await this.props.data.cartConfiguration;
            this.setState({ isQuantityLimitsFeatureEnabled: !(cartConfiguration?.IgnoreOrderSettings ?? false) });
            return;
        }
    }
}

/**
 * Functional wrapper around the cart icon controller that adds cart-change
 * detection (legacy header cart-update behavior) without altering the
 * existing mini-cart rendering.
 */
const ExtCartIcon: React.FC<ICartIconViewProps> = props => {
    const { config, resources } = props;
    const cartChangeDetection = useCartChangeDetection({
        context: props.context,
        telemetry: props.telemetry,
        data: props.data,
        cartUpdateToggle: config.cartUpdateToggle,
        displayCartUpdate: config.displayCartUpdate,
        removeUserCartItems: config.removeUserCartItems
    });

    return (
        <>
            <CartIcon {...props} />
            <CartChangePopup
                isOpen={cartChangeDetection.isPopupOpen}
                message={resources.cartUpdateMessage}
                defaultButtonMessage={resources.cartUpdateButtonMessage}
                cartLinkButtonMessage={resources.cartLinkButtonMessage}
                onClose={cartChangeDetection.closePopup}
                onGoToCart={cartChangeDetection.goToCart}
                onRemoveDetectedLines={config.removeUserCartItems ? cartChangeDetection.removeDetectedLines : undefined}
            />
        </>
    );
};

export default ExtCartIcon;
